import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, message } from 'antd';
import { CheckOutlined, FolderOutlined, IdcardOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { ChatPanel, type ChatMessage, type ChatParticipant, type QuickReply } from '../../../components/chat';
import { useTranslation } from '../../../i18n';
import { useStore } from '../../../store/useStore';
import { userInitial } from '../../../utils/userInitial';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import {
  archiveDmConversation,
  getDmCampaigns,
  markDmConversationRead,
  getDmConversations,
  getDmPendingReplyCount,
  getDmMessages,
  sendDmMessage,
  starDmConversation
} from '../../../store/request';
import type { DmCampaignListItem } from '../../../types/dm';
import type { DmConversation, DmConversationFilter, DmMessage, DmNewMessageEvent } from '../../../types/dm';
import { useMerchantRealtime } from '../layout/merchantRealtimeContext';
import type { Conversation, InboxTab } from '../../../services/messageService';
import { InboxList } from './InboxList';
import { PrivateMessageNav, type PrivateMessageNavKey } from './PrivateMessageNav';
import { BroadcastTaskList } from './BroadcastTaskList';
import { CreateBroadcastModal } from './CreateBroadcastModal';
const CAMPAIGN_PAGE_SIZE = 20;

const DM_PAGE_SIZE = 20;
const AVATAR_COLORS = ['#596ae8', '#0f9f6e', '#d97706', '#db2777', '#0891b2', '#7c3aed'];
const EMPTY_TAB_COUNTS: Record<InboxTab, number> = {
  all: 0,
  unread: 0,
  unreplied: 0,
  history: 0,
  starred: 0,
  archived: 0
};

function serverFilterOf(tab: InboxTab): DmConversationFilter {
  if (tab === 'unread') return 'UNREAD';
  if (tab === 'unreplied') return 'UNREPLIED';
  return 'ALL';
}

function influencerIdOf(value?: string | number | null) {
  if (value == null) return '';
  return String(value).trim();
}

function conversationKey(item: DmConversation) {
  if (item.id != null) return String(item.id);
  return item.platformConversationId?.trim() || '';
}

function avatarColor(seed: string) {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash];
}

function toConversation(item: DmConversation): Conversation | null {
  const id = conversationKey(item);
  if (!id) return null;
  const username = item.username?.trim() || '';
  const name = username || '—';
  return {
    id,
    talentId: item.influencerId?.trim() || '',
    peer: {
      name,
      initial: name.slice(0, 1).toUpperCase() || '?',
      color: avatarColor(id),
      avatarUrl: item.avatarUrl?.trim() || undefined
    },
    handle: username ? `@${username.replace(/^@/, '')}` : '—',
    region: '',
    category: '',
    lastMessagePreview: item.lastMessagePreview?.trim() || '',
    lastMessageAt: item.lastMessageTime?.trim() || '',
    unreadCount: item.unreadCount ?? 0,
    archived: Boolean(item.archived),
    starred: Boolean(item.starred),
    outgoingSinceReply: item.sentBeforeReplyCount ?? 0
  };
}

function matchesLocalTab(item: Conversation, tab: InboxTab) {
  if (tab === 'archived') return item.archived;
  if (tab === 'starred') return item.starred && !item.archived;
  if (tab === 'unread') return item.unreadCount > 0 && !item.archived;
  if (tab === 'all') return !item.archived;
  return true;
}

function dmPushMessageId(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return undefined;
}

function dmMessageKey(item: DmMessage) {
  if (item.id != null) return `local:${item.id}`;
  const platformId = item.platformMessageId?.trim();
  return platformId ? `platform:${platformId}` : '';
}

function directionOf(value?: string): ChatMessage['direction'] | undefined {
  const direction = value?.trim().toUpperCase();
  if (direction === 'OUTBOUND') return 'out';
  if (direction === 'INBOUND') return 'in';
  return undefined;
}

function contentTypeOf(value?: string): ChatMessage['contentType'] {
  const type = value?.trim().toUpperCase();
  if (type === 'IMAGE') return 'image';
  if (type === 'FILE') return 'file';
  return 'text';
}

function statusOf(value?: string): ChatMessage['status'] | undefined {
  const status = value?.trim().toUpperCase();
  if (status === 'SENDING') return 'sending';
  if (status === 'SENT') return 'sent';
  if (status === 'DELIVERED') return 'delivered';
  if (status === 'READ') return 'read';
  if (status === 'FAILED') return 'failed';
  return undefined;
}

function toChatMessage(item: DmMessage, index: number): ChatMessage {
  const type = contentTypeOf(item.msgType);
  const body = item.content?.trim() || '';
  const snapshot = item.contentSnapshot?.trim() || '';
  const contentType = (type === 'image' || type === 'file') && !body ? 'text' : type;
  const status = statusOf(item.deliveryStatus);
  return {
    id: dmMessageKey(item) || `dm-msg-${index}`,
    direction: directionOf(item.direction) ?? 'in',
    contentType,
    content: body || snapshot,
    ...(contentType === 'file' && snapshot ? { fileName: snapshot } : {}),
    createdAt: item.occurredAt?.trim() || '',
    ...(status ? { status } : {})
  };
}

function orderedMessages(items: DmMessage[]) {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftTime = left.item.occurredAt?.trim() || '';
      const rightTime = right.item.occurredAt?.trim() || '';
      if (leftTime && rightTime && leftTime !== rightTime) return leftTime < rightTime ? -1 : 1;
      return left.index - right.index;
    })
    .map((entry) => entry.item);
}

export const PrivateMessage = observer(() => {
  const { t } = useTranslation();
  const { loginStore } = useStore();
  const account = useCurrentAccount();
  const { shopId } = useSelectedShop();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<Exclude<PrivateMessageNavKey, 'createBroadcast'>>('inbox');
  const [createOpen, setCreateOpen] = useState(false);
  const [campaigns, setCampaigns] = useState<DmCampaignListItem[]>([]);
  const [campaignTotal, setCampaignTotal] = useState(0);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignReload, setCampaignReload] = useState(0);
  const [records, setRecords] = useState<DmConversation[]>([]);
  const [starring, setStarring] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [sending, setSending] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string>();
  const [tab, setTab] = useState<InboxTab>('all');
  const [keyword, setKeyword] = useState('');
  const [username, setUsername] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [pendingReplyCount, setPendingReplyCount] = useState(0);
  const [pendingReplyReload, setPendingReplyReload] = useState(0);
  const [activeId, setActiveId] = useState<string>();
  const [rawMessages, setRawMessages] = useState<DmMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadLoadingEarlier, setThreadLoadingEarlier] = useState(false);
  const [threadHasMore, setThreadHasMore] = useState(false);
  const [threadToken, setThreadToken] = useState<string>();
  const [threadInfluencerId, setThreadInfluencerId] = useState('');
  const listEpoch = useRef(0);
  const messageEpoch = useRef(0);
  const listBusyRef = useRef(false);
  const messageBusyRef = useRef(false);
  const queuedDmRef = useRef<DmNewMessageEvent[]>([]);
  const flushDmRef = useRef<() => void>(() => undefined);
  const recordsRef = useRef(records);
  const rawMessagesRef = useRef(rawMessages);
  const shopIdRef = useRef('');
  const activeIdRef = useRef(activeId);
  const platformConversationIdRef = useRef('');
  const pendingLockRef = useRef({ running: false, again: false });

  recordsRef.current = records;
  rawMessagesRef.current = rawMessages;
  activeIdRef.current = activeId;

  const selfName = account?.displayName?.trim() || loginStore.data.userinfo?.userName?.trim() || '';
  const selfParticipant = useMemo<ChatParticipant>(
    () => ({ name: t('chat.me'), initial: userInitial(selfName), color: '#596ae8' }),
    [selfName, t],
  );

  const quickReplies = useMemo<QuickReply[]>(
    () => [
      { key: 'sample', label: t('message.qr_sample') },
      { key: 'video', label: t('message.qr_video') },
      { key: 'commission', label: t('message.qr_commission') },
      { key: 'thanks', label: t('message.qr_thanks') },
    ],
    [t],
  );

  const threadParam = searchParams.get('thread');
  const currentShopId = shopId?.trim() || '';
  shopIdRef.current = currentShopId;
  const conversations = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    const source = records.filter((item) => {
      if (!query) return true;
      const text = `${item.username || ''} ${item.lastMessagePreview || ''}`.toLowerCase();
      return text.includes(query);
    });
    return source.flatMap((item) => {
      const mapped = toConversation(item);
      return mapped ? [mapped] : [];
    });
  }, [records, keyword]);
  const visibleConversations = useMemo(
    () => conversations.filter((item) => matchesLocalTab(item, tab)),
    [conversations, tab]
  );
  const openedRecord = records.find((item) => conversationKey(item) === activeId);
  const conversation =
    conversations.find((item) => item.id === activeId) ?? (openedRecord ? toConversation(openedRecord) ?? undefined : undefined);
  const platformConversationId =
    records.find((item) => conversationKey(item) === activeId)?.platformConversationId?.trim() || '';
  platformConversationIdRef.current = platformConversationId;
  const chatMessages = useMemo(
    () => orderedMessages(rawMessages).map((item, index) => toChatMessage(item, index)),
    [rawMessages]
  );
  const unreadTotal = conversations.filter((item) => !item.archived).reduce((sum, item) => sum + item.unreadCount, 0);

  useEffect(() => {
    const timer = window.setTimeout(() => setUsername(keyword.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    const epoch = listEpoch.current + 1;
    listEpoch.current = epoch;
    if (!currentShopId) {
      recordsRef.current = [];
      listBusyRef.current = false;
      setRecords([]);
      setHasMore(false);
      setNextPageToken(undefined);
      setInboxLoading(false);
      flushDmRef.current();
      return;
    }
    let cancelled = false;
    listBusyRef.current = true;
    recordsRef.current = [];
    setInboxLoading(true);
    setRecords([]);
    setHasMore(false);
    setNextPageToken(undefined);
    const name = username.trim();
    getDmConversations({
      shopId: currentShopId,
      filter: serverFilterOf(tab),
      pageSize: DM_PAGE_SIZE,
      ...(name ? { username: name } : {})
    })
      .then((res) => {
        if (cancelled || listEpoch.current !== epoch) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('message.load_failed'));
          recordsRef.current = [];
          setRecords([]);
          return;
        }
        const page = res.content;
        const conversations = page?.conversations;
        const list = Array.isArray(conversations) ? conversations : [];
        recordsRef.current = list;
        setRecords(list);
        const token = page?.nextPageToken?.trim() || '';
        setHasMore(Boolean(page?.hasMore && token));
        setNextPageToken(token || undefined);
      })
      .catch(() => {
        if (!cancelled && listEpoch.current === epoch) message.error(t('message.load_failed'));
      })
      .finally(() => {
        if (cancelled || listEpoch.current !== epoch) return;
        listBusyRef.current = false;
        setInboxLoading(false);
        flushDmRef.current();
      });
    return () => {
      cancelled = true;
    };
  }, [currentShopId, tab, username, reloadKey, t]);

  useEffect(() => {
    if (!currentShopId) {
      setPendingReplyCount(0);
      return;
    }
    let cancelled = false;
    getDmPendingReplyCount({ shopId: currentShopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('message.pending_reply_failed'));
          setPendingReplyCount(0);
          return;
        }
        const count = res.content;
        setPendingReplyCount(typeof count === 'number' && Number.isFinite(count) ? count : 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('message.pending_reply_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [currentShopId, reloadKey, pendingReplyReload, t]);

  useEffect(() => {
    if (view !== 'broadcast') return;
    if (!currentShopId) {
      setCampaigns([]);
      setCampaignTotal(0);
      setCampaignLoading(false);
      return;
    }
    let cancelled = false;
    setCampaignLoading(true);
    getDmCampaigns({ shopId: currentShopId, pageNum: campaignPage, pageSize: CAMPAIGN_PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('message.campaign_load_failed'));
          setCampaigns([]);
          setCampaignTotal(0);
          return;
        }
        const page = res.content;
        const list = page?.campaigns;
        setCampaigns(Array.isArray(list) ? list : []);
        setCampaignTotal(typeof page?.total === 'number' ? page.total : 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('message.campaign_load_failed'));
      })
      .finally(() => {
        if (!cancelled) setCampaignLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view, currentShopId, campaignPage, campaignReload, t]);

  useEffect(() => {
    const epoch = messageEpoch.current + 1;
    messageEpoch.current = epoch;
    if (!currentShopId || !platformConversationId) {
      rawMessagesRef.current = [];
      messageBusyRef.current = false;
      setRawMessages([]);
      setThreadHasMore(false);
      setThreadToken(undefined);
      setThreadInfluencerId('');
      setThreadLoading(false);
      flushDmRef.current();
      return;
    }
    let cancelled = false;
    messageBusyRef.current = true;
    rawMessagesRef.current = [];
    setThreadLoading(true);
    setRawMessages([]);
    setThreadHasMore(false);
    setThreadToken(undefined);
    setThreadInfluencerId('');
    getDmMessages(platformConversationId, { shopId: currentShopId, pageSize: DM_PAGE_SIZE })
      .then((res) => {
        if (cancelled || messageEpoch.current !== epoch) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('message.message_load_failed'));
          rawMessagesRef.current = [];
          setRawMessages([]);
          return;
        }
        const page = res.content;
        const messages = page?.messages;
        const list = Array.isArray(messages) ? messages : [];
        rawMessagesRef.current = list;
        setRawMessages(list);
        const token = page?.nextPageToken?.trim() || '';
        setThreadHasMore(Boolean(page?.hasMore && token));
        setThreadToken(token || undefined);
        const influencerId = influencerIdOf(page?.influencerId);
        if (influencerId) setThreadInfluencerId(influencerId);
      })
      .catch(() => {
        if (!cancelled && messageEpoch.current === epoch) message.error(t('message.message_load_failed'));
      })
      .finally(() => {
        if (cancelled || messageEpoch.current !== epoch) return;
        messageBusyRef.current = false;
        setThreadLoading(false);
        flushDmRef.current();
      });
    return () => {
      cancelled = true;
    };
  }, [currentShopId, platformConversationId, reloadKey, t]);

  useEffect(() => {
    setActiveId((current) => {
      if (current && visibleConversations.some((item) => item.id === current)) return current;
      const hiddenByKeyword =
        Boolean(keyword.trim()) && Boolean(current) && records.some((item) => conversationKey(item) === current);
      if (hiddenByKeyword) return current;
      if (visibleConversations.length === 0) return undefined;
      const fromQuery = threadParam && visibleConversations.some((item) => item.id === threadParam) ? threadParam : undefined;
      return fromQuery || visibleConversations[0].id;
    });
  }, [visibleConversations, threadParam, keyword, records]);

  const selectConversation = (id: string) => {
    setActiveId(id);
    setSearchParams({ thread: id }, { replace: true });
  };

  const loadMore = () => {
    if (!currentShopId || !hasMore || !nextPageToken || inboxLoading || loadingMore || listBusyRef.current) return;
    const epoch = listEpoch.current;
    const token = nextPageToken;
    const name = username.trim();
    listBusyRef.current = true;
    setLoadingMore(true);
    getDmConversations({
      shopId: currentShopId,
      filter: serverFilterOf(tab),
      pageSize: DM_PAGE_SIZE,
      pageToken: token,
      ...(name ? { username: name } : {})
    })
      .then((res) => {
        if (listEpoch.current !== epoch) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('message.load_failed'));
          return;
        }
        const page = res.content;
        const conversations = page?.conversations;
        const list = Array.isArray(conversations) ? conversations : [];
        const seen = new Set(recordsRef.current.map(conversationKey));
        const merged = [
          ...recordsRef.current,
          ...list.filter((item) => {
            const id = conversationKey(item);
            return id && !seen.has(id);
          })
        ];
        recordsRef.current = merged;
        setRecords(merged);
        const next = page?.nextPageToken?.trim() || '';
        setHasMore(Boolean(page?.hasMore && next));
        setNextPageToken(next || undefined);
      })
      .catch(() => {
        if (listEpoch.current === epoch) message.error(t('message.load_failed'));
      })
      .finally(() => {
        if (listEpoch.current !== epoch) return;
        listBusyRef.current = false;
        setLoadingMore(false);
        flushDmRef.current();
      });
  };

  const handleNav = (key: PrivateMessageNavKey) => {
    if (key === 'createBroadcast') {
      setCreateOpen(true);
    } else {
      setView(key);
    }
  };

  const createBroadcast = () => {
    setCampaignPage(1);
    setCampaignReload((version) => version + 1);
    setView('broadcast');
  };

  const patchConversation = (id: string, patch: Partial<DmConversation>) => {
    const next = recordsRef.current.map((item) => (conversationKey(item) === id ? { ...item, ...patch } : item));
    recordsRef.current = next;
    setRecords(next);
  };

  const toggleArchive = async () => {
    if (!conversation || archiving) return;
    if (!platformConversationId || !currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const next = !conversation.archived;
    setArchiving(true);
    try {
      const res = await archiveDmConversation(platformConversationId, { shopId: currentShopId, archived: next });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('message.archive_failed'));
        return;
      }
      const archived = res.content?.archived ?? next;
      patchConversation(conversation.id, { archived });
      message.success(res.message || (archived ? t('message.archived_toast') : t('message.restored_toast')));
    } catch {
      message.error(t('message.archive_failed'));
    } finally {
      setArchiving(false);
    }
  };

  const markRead = async () => {
    if (!conversation || markingRead || conversation.unreadCount <= 0) return;
    if (!platformConversationId || !currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const userId = account?.userCode?.trim();
    setMarkingRead(true);
    try {
      const res = await markDmConversationRead(platformConversationId, {
        shopId: currentShopId,
        ...(userId ? { userId } : {})
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('message.mark_read_failed'));
        return;
      }
      const unreadCount = res.content?.unreadCount;
      patchConversation(conversation.id, {
        unreadCount: typeof unreadCount === 'number' ? unreadCount : 0
      });
      message.success(res.message || t('message.marked_read'));
    } catch {
      message.error(t('message.mark_read_failed'));
    } finally {
      setMarkingRead(false);
    }
  };

  const toggleStar = async () => {
    if (!conversation || starring) return;
    if (!platformConversationId || !currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      return;
    }
    const next = !conversation.starred;
    setStarring(true);
    try {
      const res = await starDmConversation(platformConversationId, { shopId: currentShopId, starred: next });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('message.star_failed'));
        return;
      }
      const starred = res.content?.starred ?? next;
      patchConversation(conversation.id, { starred });
      message.success(res.message || (starred ? t('message.starred_toast') : t('message.unstarred_toast')));
    } catch {
      message.error(t('message.star_failed'));
    } finally {
      setStarring(false);
    }
  };

  const sendText = async (text: string) => {
    const content = text.trim();
    if (!content || sending) return;
    if (!platformConversationId || !currentShopId) {
      message.warning(t('invitation.invite_shop_required'));
      throw new Error('send');
    }
    setSending(true);
    try {
      const res = await sendDmMessage(platformConversationId, {
        shopId: currentShopId,
        msgType: 'TEXT',
        content
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('message.send_failed'));
        throw new Error('send');
      }
      const result = res.content;
      const sentAt = new Date().toISOString();
      const sentMessage: DmMessage = {
        id: result?.messageId,
        platformMessageId: result?.platformMessageId,
        direction: 'OUTBOUND',
        msgType: 'TEXT',
        content,
        occurredAt: sentAt
      };
      const nextMessages = [...rawMessagesRef.current, sentMessage];
      rawMessagesRef.current = nextMessages;
      setRawMessages(nextMessages);
      if (conversation) {
        patchConversation(conversation.id, {
          lastMessagePreview: content,
          lastMessageTime: sentAt,
          ...(typeof result?.sentBeforeReplyCount === 'number' ? { sentBeforeReplyCount: result.sentBeforeReplyCount } : {})
        });
      }
      message.success(res.message || result?.message || t('message.send_success'));
      if (currentShopId) setPendingReplyReload((version) => version + 1);
    } catch (error) {
      if (!(error instanceof Error) || error.message !== 'send') {
        message.error(t('message.send_failed'));
      }
      throw error;
    } finally {
      setSending(false);
    }
  };

  const loadEarlierMessages = () => {
    if (!currentShopId || !platformConversationId || !threadHasMore || !threadToken || threadLoading || threadLoadingEarlier) {
      return;
    }
    const epoch = messageEpoch.current;
    const token = threadToken;
    setThreadLoadingEarlier(true);
    getDmMessages(platformConversationId, {
      shopId: currentShopId,
      pageSize: DM_PAGE_SIZE,
      pageToken: token
    })
      .then((res) => {
        if (messageEpoch.current !== epoch) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('message.history_failed'));
          return;
        }
        const page = res.content;
        const messages = page?.messages;
        const incoming = Array.isArray(messages) ? messages : [];
        const seen = new Set(rawMessagesRef.current.map(dmMessageKey).filter(Boolean));
        const nextMessages = [
          ...rawMessagesRef.current,
          ...incoming.filter((item) => {
            const id = dmMessageKey(item);
            return !id || !seen.has(id);
          })
        ];
        rawMessagesRef.current = nextMessages;
        setRawMessages(nextMessages);
        const next = page?.nextPageToken?.trim() || '';
        setThreadHasMore(Boolean(page?.hasMore && next));
        const influencerId = influencerIdOf(page?.influencerId);
        if (influencerId) setThreadInfluencerId(influencerId);
        setThreadToken(next || undefined);
      })
      .catch(() => {
        if (messageEpoch.current === epoch) message.error(t('message.history_failed'));
      })
      .finally(() => {
        if (messageEpoch.current === epoch) setThreadLoadingEarlier(false);
      });
  };

  const schedulePendingReply = () => {
    const lock = pendingLockRef.current;
    const run = () => {
      const currentShop = shopIdRef.current;
      if (!currentShop) return;
      if (lock.running) {
        lock.again = true;
        return;
      }
      lock.running = true;
      getDmPendingReplyCount({ shopId: currentShop })
        .then((res) => {
          if (shopIdRef.current !== currentShop || res.code !== 200) return;
          const count = res.content;
          if (typeof count === 'number' && Number.isFinite(count)) setPendingReplyCount(count);
        })
        .catch(() => undefined)
        .finally(() => {
          lock.running = false;
          if (lock.again) {
            lock.again = false;
            run();
          }
        });
    };
    run();
  };

  const applyDmPush = (event: DmNewMessageEvent) => {
    const currentShop = shopIdRef.current;
    if (!currentShop || event.shopId == null || String(event.shopId) !== currentShop) return;
    if (event.message?.direction?.trim().toUpperCase() !== 'INBOUND') return;
    const id = event.conversationId != null ? String(event.conversationId) : '';
    if (!id) return;
    const unread = event.conversation?.unreadCount;
    const preview = event.message?.content?.trim() || '';
    const receivedAt = new Date().toISOString();

    if (activeIdRef.current === id) {
      const incoming: DmMessage = {
        id: dmPushMessageId(event.message?.id),
        direction: 'INBOUND',
        msgType: event.message?.msgType?.trim() || 'TEXT',
        content: event.message?.content || '',
        occurredAt: receivedAt
      };
      const key = dmMessageKey(incoming);
      const exists = key ? rawMessagesRef.current.some((item) => dmMessageKey(item) === key) : false;
      if (!exists) {
        const nextMessages = [...rawMessagesRef.current, incoming];
        rawMessagesRef.current = nextMessages;
        setRawMessages(nextMessages);
      }
    }

    const current = recordsRef.current.find((item) => conversationKey(item) === id);
    if (current) {
      const nextItem: DmConversation = {
        ...current,
        unreadCount: typeof unread === 'number' ? unread : current.unreadCount,
        lastMessagePreview: preview || current.lastMessagePreview,
        lastMessageTime: receivedAt
      };
      const nextRecords = [nextItem, ...recordsRef.current.filter((item) => conversationKey(item) !== id)];
      recordsRef.current = nextRecords;
      setRecords(nextRecords);
    } else if (!listBusyRef.current) {
      listBusyRef.current = true;
      setReloadKey((value) => value + 1);
    }
    schedulePendingReply();
  };

  const enqueueDmPush = (event: DmNewMessageEvent) => {
    if (listBusyRef.current || messageBusyRef.current) {
      queuedDmRef.current.push(event);
      return;
    }
    applyDmPush(event);
  };

  flushDmRef.current = () => {
    if (listBusyRef.current || messageBusyRef.current) return;
    const queued = queuedDmRef.current.splice(0);
    queued.forEach((event) => applyDmPush(event));
  };

  useMerchantRealtime((event) => {
    if (event.event === 'DM_SOCKET_RECONNECTED') {
      if (!shopIdRef.current) return;
      listBusyRef.current = true;
      if (platformConversationIdRef.current) messageBusyRef.current = true;
      setReloadKey((value) => value + 1);
      return;
    }
    if (event.event !== 'DM_NEW_MESSAGE') return;
    enqueueDmPush(event as DmNewMessageEvent);
  });

  const headerSubtitle =
    conversation ?
      <span className='inline-flex items-center gap-1.5'>
        {conversation.peer.online == null ? null : (
          <span className={`size-1.5 rounded-full ${conversation.peer.online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
        )}
        {conversation.peer.online == null ? conversation.handle : `${conversation.peer.online ? t('chat.online') : t('message.offline')} · ${conversation.handle}`}
      </span>
      : null;

  const headerActions =
    conversation ?
      <div className='flex items-center gap-2'>
        {conversation.unreadCount > 0 ? (
          <Button
            size='small'
            icon={<CheckOutlined />}
            className='chat-header-action rounded-md'
            loading={markingRead}
            onClick={markRead}>
            {t('message.mark_read')}
          </Button>
        ) : null}
        <Button
          size='small'
          icon={conversation.starred ? <StarFilled className='text-amber-400' /> : <StarOutlined />}
          className={`chat-header-action rounded-md ${conversation.starred ? 'border-amber-300 text-amber-500' : ''}`}
          onClick={toggleStar}>
          {conversation.starred ? t('message.starred') : t('message.star')}
        </Button>
        <Button
          size='small'
          icon={<FolderOutlined />}
          className='chat-header-action rounded-md'
          loading={archiving}
          onClick={toggleArchive}>
          {conversation.archived ? t('message.restore') : t('message.archive')}
        </Button>
        {(threadInfluencerId || conversation.talentId.trim()) ? (
          <Button
            size='small'
            icon={<IdcardOutlined />}
            className='chat-header-action rounded-md'
            href={`/creator/talent_square/${encodeURIComponent(threadInfluencerId || conversation.talentId.trim())}`}
            target='_blank'
            rel='noopener noreferrer'>
            {t('message.view_profile')}
          </Button>
        ) : (
          <Button
            size='small'
            icon={<IdcardOutlined />}
            className='chat-header-action rounded-md'
            onClick={() => message.warning(t('message.profile_missing'))}>
            {t('message.view_profile')}
          </Button>
        )}
      </div>
      : null;

  return (
    <div className="container flex flex-col !overflow-hidden">
      <header className="mb-4 flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="mb-0 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">
          {t('message.title')}
        </h1>
      </header>
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_4px_16px_rgba(0,0,0,0.04)]'>
        <PrivateMessageNav activeKey={view} unreadTotal={unreadTotal} onSelect={handleNav} />
        {view === 'broadcast' ? (
          <BroadcastTaskList
            campaigns={campaigns}
            total={campaignTotal}
            pageNum={campaignPage}
            pageSize={CAMPAIGN_PAGE_SIZE}
            loading={campaignLoading}
            onPageChange={setCampaignPage}
          />
        ) : null}
        <div className={`min-h-0 flex-1 ${view === 'inbox' ? 'flex' : 'hidden'}`}>
          <InboxList
            conversations={visibleConversations}
            activeId={activeId}
            tab={tab}
            tabCounts={{ ...EMPTY_TAB_COUNTS, unreplied: pendingReplyCount }}
            keyword={keyword}
            loading={inboxLoading}
            loadingMore={loadingMore}
            onTabChange={setTab}
            onKeywordChange={setKeyword}
            onSelect={selectConversation}
            onLoadMore={loadMore}
            onRefresh={() => {
              if (!currentShopId) {
                message.warning(t('invitation.invite_shop_required'));
                return;
              }
              setReloadKey((version) => version + 1);
            }}
          />
          <main className='min-w-0 flex-1'>
            <ChatPanel
              conversationKey={conversation?.id}
              peer={conversation?.peer}
              self={selfParticipant}
              messages={chatMessages}
              headerSubtitle={headerSubtitle}
              headerActions={headerActions}
              quickReplies={quickReplies}
              hasMore={threadHasMore}
              loadingEarlier={threadLoading || threadLoadingEarlier}
              onLoadEarlier={loadEarlierMessages}
              onSend={sendText}
              sending={sending}
              emptyText={t('message.empty')}
            />
          </main>
        </div>
        <CreateBroadcastModal
          open={createOpen}
          shopId={currentShopId}
          onClose={() => setCreateOpen(false)}
          onCreated={createBroadcast}
        />
      </div>
    </div>

  );
});
